/**
 * A request that never answers must end.
 *
 * The database cuts off a slow statement itself; nothing cuts off a request
 * whose connection went quiet — a phone moving between networks, a proxy that
 * swallowed it. Without this the screen waiting on it shows its skeleton for
 * as long as the tab is open. With it, the request fails, the screen's own
 * error state appears, and "Try again" is one press away.
 *
 * A caller's own signal still works: cancelling it cancels the request.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

export function fetchWithTimeout(
    timeoutMs = REQUEST_TIMEOUT_MS,
    underlying: typeof fetch = (...args) => fetch(...args),
): typeof fetch {
    return async (input, init = {}) => {
        const controller = new AbortController();
        const timer = setTimeout(
            () => controller.abort(new DOMException('The request took too long to answer.', 'TimeoutError')),
            timeoutMs,
        );
        const outer = init.signal;
        if (outer?.aborted) {
            clearTimeout(timer);
            throw outer.reason;
        }
        outer?.addEventListener('abort', () => controller.abort(outer.reason), { once: true });
        try {
            return await underlying(input, { ...init, signal: controller.signal });
        } finally {
            clearTimeout(timer);
        }
    };
}
